import { Locale } from '@headless-workspace/config';
import { LanguageHelpers } from '@headless-workspace/utils';
import { AuthorizationStrategy, CacheStrategy, QueryParamStrategy, RefreshTokenStrategy } from '../../strategy';
import { ApiBehaviourDelegate, ApiDatasource, ApiRequestOptions } from '../api';
import { HttpMethod } from '../http';
import { BffBehaviourDelegate, QUERY_PARAMS } from './BffBehaviourDelegate';

export class BffDatasource implements ApiDatasource {
    constructor(
        readonly baseUrl: string,
        authStrategyProvider: Partial<AuthorizationStrategy>,
        refreshTokenStrategy: Partial<RefreshTokenStrategy>,
        cacheStrategy: CacheStrategy,
        queryParamStrategy: Partial<QueryParamStrategy>,
        private readonly apiBehaviourDelegate: ApiBehaviourDelegate = new BffBehaviourDelegate(
            authStrategyProvider,
            refreshTokenStrategy,
            cacheStrategy,
            queryParamStrategy,
        ),
    ) {}

    private getLocale(params?: Record<string, unknown> | undefined): Locale | undefined {
        const localeParam = params?.[QUERY_PARAMS.LOCALE] as string | undefined;
        return localeParam ? LanguageHelpers.toLocale(localeParam) : undefined;
    }

    private async callDatasource<T>(
        path: string,
        method: HttpMethod,
        body?: unknown,
        params?: Record<string, unknown>,
        headers?: Record<string, string>,
    ): Promise<T> {
        const url = this.apiBehaviourDelegate.buildUrl(this.baseUrl, path, params);

        const baseOptions: ApiRequestOptions = { locale: this.getLocale(params), headers };
        const options = body ? { ...baseOptions, body } : baseOptions;

        const response = await this.apiBehaviourDelegate.callResource(url, method, options);
        return response.data as T;
    }

    async getResource<T>(
        path: string,
        params?: Record<string, unknown> | undefined,
        headers?: Record<string, string>,
    ): Promise<T | null> {
        return this.callDatasource<T>(path, HttpMethod.GET, undefined, params, headers);
    }

    async createResource<Input, Output>(
        path: string,
        resource: Input,
        params?: Record<string, unknown>,
        headers?: Record<string, string>,
    ): Promise<Output> {
        return this.callDatasource<Output>(path, HttpMethod.POST, resource, params, headers);
    }

    async patchResource<Input, Output = Input>(
        path: string,
        resource: Input,
        params?: Record<string, unknown>,
    ): Promise<Output> {
        return this.callDatasource<Output>(path, HttpMethod.PATCH, resource, params);
    }

    async deleteResource<Input, Output = Input>(
        path: string,
        resource: Input,
        params?: Record<string, unknown>,
    ): Promise<Output> {
        return this.callDatasource<Output>(path, HttpMethod.DELETE, resource, params);
    }
}
